import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
	isApiAlertActive,
	isTokenInvalidText,
	showSessionExpiredAlert,
} from "./apiAlert";

const TOAST_HOST_ID = "toastify-host";
const CUSTOM_TOAST_HOST_ID = "custom-toastify-host";
const MOBILE_BREAKPOINT = 768;

let toastRoot = null;
let customToastRoot = null;
let mobileToastTimer = null;
let activeMobileToastId = null;

const isMobileView = () => {
	if (typeof window === "undefined") return false;
	return window.innerWidth <= MOBILE_BREAKPOINT;
};

const toMessage = (msg) => {
	if (msg == null || msg === "") return "";
	return typeof msg === "string" ? msg : String(msg);
};

/**
 * Build a stable toastId from the message so react-toastify suppresses
 * duplicates of the same message while one is still visible. This prevents
 * the "same error toast appears 2-3 times" issue when both the API layer and
 * page code report the same failure.
 */
const toastId = (type, m) => `${type}:${m}`;

const showCustomMobilePopup = (type, msg) => {
	if (!isMobileView() || typeof document === "undefined") return false;

	const id = toastId(type, msg);
	if (activeMobileToastId === id) return true;
	activeMobileToastId = id;

	let hostEl = document.getElementById(CUSTOM_TOAST_HOST_ID);
	if (!hostEl) {
		hostEl = document.createElement("div");
		hostEl.id = CUSTOM_TOAST_HOST_ID;
		hostEl.style.position = "fixed";
		hostEl.style.inset = "0";
		hostEl.style.zIndex = "20000";
		hostEl.style.pointerEvents = "auto";
		document.body.appendChild(hostEl);
	}

	if (!customToastRoot) customToastRoot = createRoot(hostEl);

	const config = {
		success: { label: "Success", icon: "✓", ring: "#16a34a", bg: "#ecfdf5", text: "#14532d", accent: "#22c55e" },
		error: { label: "Error", icon: "!", ring: "#b91c1c", bg: "#fef2f2", text: "#7f1d1d", accent: "#ef4444" },
		warn: { label: "Warning", icon: "⚠", ring: "#d97706", bg: "#fff7ed", text: "#9a5b00", accent: "#f59e0b" },
		info: { label: "Info", icon: "i", ring: "#1d4ed8", bg: "#eff6ff", text: "#1e3a8a", accent: "#3b82f6" },
	};
	const selected = config[type] || config.info;

	customToastRoot.render(
		<div
			style={{
				position: "fixed",
				inset: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "16px",
				background: "rgba(15, 23, 42, 0.35)",
				pointerEvents: "auto",
			}}
		>
			<div
				style={{
					width: "min(360px, 88vw)",
					borderRadius: "22px",
					background: selected.bg,
					border: `1px solid ${selected.ring}`,
					boxShadow: "0 18px 45px rgba(15, 23, 42, 0.25)",
					padding: "18px 18px 16px",
					textAlign: "center",
					color: selected.text,
					fontFamily: "system-ui, sans-serif",
				}}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						marginBottom: "12px",
					}}
				>
					<div
						style={{
							width: "44px",
							height: "44px",
							borderRadius: "50%",
							background: selected.accent,
							color: "#ffffff",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "22px",
							fontWeight: 700,
							boxShadow: "0 8px 18px rgba(0,0,0,0.15)",
						}}
					>
						{selected.icon}
					</div>
				</div>
				<div
					style={{
						fontSize: "18px",
						fontWeight: 800,
						marginBottom: "8px",
						letterSpacing: "0.2px",
					}}
				>
					{selected.label}
				</div>
				<div
					style={{
						fontSize: "14px",
						lineHeight: 1.5,
						fontWeight: 600,
						wordBreak: "break-word",
					}}
				>
					{msg}
				</div>
			</div>
		</div>
	);

	if (mobileToastTimer) window.clearTimeout(mobileToastTimer);
	mobileToastTimer = window.setTimeout(() => {
		activeMobileToastId = null;
		if (customToastRoot) {
			try {
				// unmount the React root to release any React-managed event handlers
				if (typeof customToastRoot.unmount === "function") customToastRoot.unmount();
			} catch (e) {}
			customToastRoot = null;
		}

		// remove the host element so it no longer intercepts pointer events
		try {
			const host = document.getElementById(CUSTOM_TOAST_HOST_ID);
			if (host && host.parentNode) host.parentNode.removeChild(host);
		} catch (e) {}
	}, 2200);

	return true;
};

export const notifySuccess = (msg) => {
	if (isApiAlertActive()) return;
	const m = toMessage(msg);
	if (!m) return;
	if (showCustomMobilePopup("success", m)) return;
	toast.success(m, { toastId: toastId("success", m) });
};

export const notifyError = (msg) => {
	if (isApiAlertActive()) return;
	const m = toMessage(msg);
	if (!m) return;

	if (isTokenInvalidText(m)) {
		showSessionExpiredAlert();
		return;
	}

	if (isApiAlertActive()) return;
	if (showCustomMobilePopup("error", m)) return;
	toast.error(m, { toastId: toastId("error", m) });
};

export const notifyWarn = (msg) => {
	if (isApiAlertActive()) return;
	const m = toMessage(msg);
	if (!m) return;
	if (showCustomMobilePopup("warn", m)) return;
	toast.warn(m, { toastId: toastId("warn", m) });
};

export const notifyInfo = (msg) => {
	if (isApiAlertActive()) return;
	const m = toMessage(msg);
	if (!m) return;
	if (showCustomMobilePopup("info", m)) return;
	toast.info(m, { toastId: toastId("info", m) });
};

const mountToastContainer = () => {
	if (typeof document === "undefined" || toastRoot) return;

	let hostEl = document.getElementById(TOAST_HOST_ID);
	if (!hostEl) {
		hostEl = document.createElement("div");
		hostEl.id = TOAST_HOST_ID;
		document.body.appendChild(hostEl);
	}

	toastRoot = createRoot(hostEl);
	toastRoot.render(
		<ToastContainer
			position="top-right"
			autoClose={2000}
			hideProgressBar
			newestOnTop={false}
			closeOnClick
			rtl={false}
			pauseOnFocusLoss={false}
			draggable
			pauseOnHover
			theme="colored"
			style={{ zIndex: 20000 }}
		/>
	);
};

/** Mount once at app root; safe to render from any page. */
const Toastify = () => {
	useEffect(() => {
		mountToastContainer();
	}, []);

	return null;
};

export default Toastify;
